import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import Navbar from './Navbar';
import VaultoLogo from './VaultoLogo';
import Footer from './Footer';
import { trackAuth, trackError, trackSubscription } from '../utils/analytics';

const Login = () => {
  const { t } = useTranslation();
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [trialStarted, setTrialStarted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Check for trial_started param (from promo campaign checkout)
  const isTrialStarted = searchParams.get('trial_started') === 'true';
  const campaignName = searchParams.get('campaign');

  // Fire tracking events when user arrives from trial checkout
  useEffect(() => {
    if (isTrialStarted && !trialStarted) {
      setTrialStarted(true);
      // Fire GA and Facebook Pixel events
      trackSubscription.startTrial(campaignName || 'promo_trial', 90, 'Premium');
      console.log('🎉 Trial started - tracking events fired');
    }
  }, [isTrialStarted, campaignName, trialStarted]);

  // Check for redirect query param first, then location state, then default to dashboard
  // If coming from trial, redirect to settings after login
  const redirectParam = searchParams.get('redirect');
  const from = isTrialStarted ? '/settings' : (redirectParam || location.state?.from?.pathname || '/dashboard');

  const onSubmit = async (data) => {
    try {
      setLoginError(''); // Clear previous errors
      const result = await login(data);
      if (result.success) {
        // Track successful login
        trackAuth.login('email');
        
        // Check if wizard mode is pending (user came from homepage with ?wizard=true)
        const wizardModePending = localStorage.getItem('wizard_mode_pending');
        if (wizardModePending === 'true') {
          localStorage.removeItem('wizard_mode_pending');
          sessionStorage.setItem('wizard_mode_active', 'true');
          navigate('/dashboard', { replace: true });
        } else {
          // Affiliates now go to regular dashboard like all other users
          navigate(from, { replace: true });
        }
      } else {
        // Track failed login attempt
        const errorMessage = result.error || 'Invalid email or password. Please try again.';
        trackError.authError('login_failed', errorMessage);
        // Set error message to display in the form
        setLoginError(errorMessage);
      }
    } catch (error) {
      // Handle any unexpected errors
      console.error('Unexpected login error:', error);
      const errorMessage = 'An unexpected error occurred. Please try again.';
      trackError.authError('login_error', errorMessage);
      setLoginError(errorMessage);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Track Google login attempt
      trackAuth.login('google');
      
      // Store redirect parameter for after OAuth callback
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        localStorage.setItem('oauth_redirect', redirectTo);
      }
      
      // Get Google auth URL from backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/login`);
      const data = await response.json();
      
      if (data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        throw new Error('Failed to get Google auth URL');
      }
    } catch (error) {
      console.error('Google login error:', error);
      trackError.authError('google_login_failed', error.message);
      setLoginError('Failed to connect with Google. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
      <div className="absolute top-0 left-0 right-0 h-full">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-32 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-10 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>
      
      <Navbar />
      <div className="relative flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Glassmorphism container */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="text-center">
              {/* Logo */}
              <div className="mx-auto mb-6">
                <VaultoLogo className="h-16 w-auto mx-auto" showText={true} />
              </div>
              
              <h2 className="text-3xl font-extrabold text-white mb-2">
                {t('login.title')}
              </h2>
              <p className="text-gray-300 text-sm">
                {t('login.subtitle')}
              </p>
              
              {/* Trust indicator */}
              <div className="mt-4 inline-flex items-center space-x-1 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-white text-xs">
                <svg className="h-3 w-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>{t('login.secureLogin')}</span>
              </div>
              
              {/* Trial Started Success Banner */}
              {isTrialStarted && (
                <div className="mt-4 p-4 bg-green-500/20 border border-green-400/30 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center space-x-2">
                    <svg className="h-5 w-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div className="text-left">
                      <p className="text-green-300 font-medium text-sm">Your 3-month Premium trial is ready!</p>
                      <p className="text-green-200/80 text-xs mt-0.5">Please log in to start using your premium features.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <form className="mt-8 space-y-6" onSubmit={(e) => e.preventDefault()} noValidate>
              {/* Google Sign In Button - Top */}
              <div className="mb-6">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center px-4 py-3 border border-white/20 rounded-xl text-white bg-white/10 hover:bg-white/20 transition-colors shadow-lg backdrop-blur-sm"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </button>
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 text-gray-400 bg-transparent">Or continue with email</span>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`appearance-none rounded-xl relative block w-full px-4 py-3 border-0 bg-white/10 backdrop-blur-sm ${
                      errors.email ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
                    } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
                    placeholder="Enter your email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-400">{errors.email.message}</p>
                  )}
                </div>
                
                <div className="relative">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={`appearance-none rounded-xl relative block w-full px-4 py-3 pr-12 border-0 bg-white/10 backdrop-blur-sm ${
                      errors.password ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
                    } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
                    placeholder="Enter your password"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 top-6 pr-4 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-2 text-sm text-red-400">{errors.password.message}</p>
                  )}
                </div>

                {/* Remember Me checkbox and Forgot Password */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember_me"
                      type="checkbox"
                      className="h-4 w-4 rounded border-white/20 bg-white/10 text-pink-500 focus:ring-pink-400 focus:ring-offset-0 cursor-pointer"
                      {...register('remember_me')}
                    />
                    <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-300 cursor-pointer">
                      {t('login.rememberMe')}
                    </label>
                  </div>
                  <div className="text-sm">
                    <Link
                      to="/forgot-password"
                      className="font-medium text-pink-400 hover:text-pink-300 transition-colors"
                    >
                      {t('login.forgotPassword')}
                    </Link>
                  </div>
                </div>
              </div>

              {/* Login error message - prominently displayed */}
              {loginError && (
                <div className="bg-red-500/30 backdrop-blur-sm border-2 border-red-400 text-white px-4 py-4 rounded-xl shadow-lg animate-shake">
                  <div className="flex items-start space-x-3">
                    <svg className="h-6 w-6 text-red-300 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="font-semibold text-sm mb-1">{t('login.loginFailed')}</p>
                      <p className="text-sm text-red-100">{loginError}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4">
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleSubmit(onSubmit)}
                  className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
                >
                  {loading ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : null}
                  {loading ? t('login.signingIn') : t('login.signIn')}
                </button>
              </div>

              <div className="text-center pt-4 border-t border-white/10">
                <p className="text-sm text-gray-300">
                  {t('login.noAccount')}{' '}
                  <Link
                    to="/register"
                    className="font-semibold text-pink-400 hover:text-pink-300 transition-colors"
                  >
                    {t('login.createAccount')}
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      <Footer variant="dark" />
    </div>
  );
};

export default Login; 